import { Component } from '@theme/component';
import { convertMajorUnitsToMinorUnits, formatMoney } from '@theme/money-formatting';

const MONEY_FORMAT_NO_DECIMALS = {
  amount: 'amount_no_decimals',
  amount_with_comma_separator: 'amount_no_decimals_with_comma_separator',
  amount_with_space_separator: 'amount_no_decimals_with_space_separator',
};

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateCurrencyAmounts();
    this.play();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #updateCurrencyAmounts() {
    const amountElements = this.querySelectorAll('[data-free-shipping-amount]');
    if (!amountElements.length) return;

    const activeCurrency = window.Shopify?.currency?.active || '';
    const rate = Number.parseFloat(window.Shopify?.currency?.rate || '1');
    if (!activeCurrency || Number.isNaN(rate)) return;

    amountElements.forEach((amountElement) => {
      if (!(amountElement instanceof HTMLElement)) return;

      const baseAmount = Number.parseFloat(amountElement.dataset.baseAmount || '');
      const baseCurrency = amountElement.dataset.baseCurrency || '';
      const rawMoneyFormat = amountElement.dataset.moneyFormat || '';
      const currencyFormat = baseCurrency ? rawMoneyFormat.replace(baseCurrency, '{{currency}}') : rawMoneyFormat;
      const moneyFormat = this.#formatWithoutDecimals(currencyFormat);
      if (Number.isNaN(baseAmount) || !moneyFormat) return;

      const convertedAmount = convertMajorUnitsToMinorUnits(Math.ceil(baseAmount * rate), activeCurrency);
      amountElement.textContent = formatMoney(convertedAmount, moneyFormat, activeCurrency);
    });
  }

  /**
   * Returns a money format that displays whole numbers only.
   * @param {string} format
   * @returns {string}
   */
  #formatWithoutDecimals(format) {
    return format.replace(/{{\s*(\w+)\s*}}/g, (match, placeholder) => {
      if (typeof placeholder !== 'string') return match;

      return `{{${MONEY_FORMAT_NO_DECIMALS[placeholder] || placeholder}}}`;
    });
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
